/**
 * shared/schema/sso.ts
 *
 * Pack 11.A — WorkOS Enterprise Authentication Platform (Task #1029).
 *
 * Three tables wire the WorkOS SSO + SCIM substrate into the platform:
 *
 *   sso_connections         — 1:1 to tenants. Mirrors the WorkOS
 *                             organization + connection state needed for
 *                             SAML / OIDC SSO and the admin-portal
 *                             handoff. Status="unconfigured" until a
 *                             tenant admin completes the WorkOS portal
 *                             flow; "active" once the IdP confirms the
 *                             handshake via the WorkOS dashboard.
 *
 *   scim_directories        — 1:1 to tenants. Mirrors the WorkOS
 *                             Directory record (SCIM provisioning
 *                             endpoint). Status driven by the WorkOS
 *                             "dsync.activated" / "dsync.deleted" webhook
 *                             events (server/routes/webhooks/scim.ts).
 *
 *   scim_user_links         — append-only join from WorkOS directory
 *                             user IDs to platform `users.id` rows. Used
 *                             by the SCIM webhook to translate idempotent
 *                             provider IDs into local user records on
 *                             user.created / user.updated / user.deleted
 *                             events without requiring `users.external_id`
 *                             column changes (zero-impact on the heavy
 *                             users table).
 *
 * The whole surface degrades gracefully when no WorkOS connector is
 * wired: rows are simply absent and the existing email/password auth
 * path remains the sole login mechanism. This honours the task's "do
 * not break the existing auth path for non-enterprise tenants" contract.
 */

import { sql } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, jsonb, index, boolean, integer, uniqueIndex } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

import { tenants, users } from "./common";

/**
 * Single-row-per-tenant WorkOS SSO connection state.
 *
 * provider:    "saml" | "oidc" | "unknown"     — the IdP protocol.
 * status:      "unconfigured" | "configuring"  — pre-active states.
 *              "active"                        — IdP handshake confirmed.
 *              "disabled"                      — admin-paused.
 *              "error"                         — last sync failed.
 */
export const ssoConnections = pgTable("sso_connections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().unique().references(() => tenants.id, { onDelete: "cascade" }),
  workosOrganizationId: text("workos_organization_id"),
  workosConnectionId: text("workos_connection_id"),
  provider: text("provider").notNull().default("unknown"),
  status: text("status").notNull().default("unconfigured"),
  domain: text("domain"),
  configuredAt: timestamp("configured_at", { withTimezone: true }),
  configuredByUserId: text("configured_by_user_id").references(() => users.id, { onDelete: "set null" }),
  lastSyncAt: timestamp("last_sync_at", { withTimezone: true }),
  lastErrorMessage: text("last_error_message"),
  metadata: jsonb("metadata").notNull().default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxSsoConnectionsWorkosOrg: index("idx_sso_connections_workos_org").on(t.workosOrganizationId),
  idxSsoConnectionsStatus: index("idx_sso_connections_status").on(t.status),
}));

export const insertSsoConnectionSchema = createInsertSchema(ssoConnections).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertSsoConnection = z.infer<typeof insertSsoConnectionSchema>;
export type SsoConnection = typeof ssoConnections.$inferSelect;

/**
 * Single-row-per-tenant WorkOS Directory (SCIM) state.
 */
export const scimDirectories = pgTable("scim_directories", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().unique().references(() => tenants.id, { onDelete: "cascade" }),
  workosDirectoryId: text("workos_directory_id"),
  workosOrganizationId: text("workos_organization_id"),
  provider: text("provider"),
  status: text("status").notNull().default("unconfigured"),
  lastEventAt: timestamp("last_event_at", { withTimezone: true }),
  lastEventType: text("last_event_type"),
  metadata: jsonb("metadata").notNull().default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxScimDirectoriesWorkosDirectory: index("idx_scim_directories_workos_directory").on(t.workosDirectoryId),
  idxScimDirectoriesStatus: index("idx_scim_directories_status").on(t.status),
}));

export const insertScimDirectorySchema = createInsertSchema(scimDirectories).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertScimDirectory = z.infer<typeof insertScimDirectorySchema>;
export type ScimDirectory = typeof scimDirectories.$inferSelect;

/**
 * Append-only join from WorkOS directory user IDs to platform users.
 *
 * `workosDirectoryUserId` is the stable identifier on WorkOS-side; we
 * never trust their `email` claim by itself for matching.
 */
export const scimUserLinks = pgTable("scim_user_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  workosDirectoryUserId: text("workos_directory_user_id").notNull().unique(),
  workosDirectoryId: text("workos_directory_id"),
  userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxScimUserLinksTenant: index("idx_scim_user_links_tenant").on(t.tenantId),
  idxScimUserLinksUser: index("idx_scim_user_links_user").on(t.userId),
}));

export const insertScimUserLinkSchema = createInsertSchema(scimUserLinks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertScimUserLink = z.infer<typeof insertScimUserLinkSchema>;
export type ScimUserLink = typeof scimUserLinks.$inferSelect;

// ─── PTW CAP-19 (Task #1372) — High-Stakes Step-Up Auth ─────────────
//
// Three tables back the new step-up auth surface that gates PTW
// high-stakes signature actions (issue / override / close /
// second-actor) AND any other guarded administrative action that
// declares a `minStepUpFactor` floor:
//
//   webauthn_credentials      — append-only registry of WebAuthn /
//                               Passkey public keys per user. The
//                               sign-counter is mutated on every
//                               successful assertion to defend against
//                               cloned authenticators. `friendlyName`
//                               lets users distinguish e.g. "iPhone
//                               Touch ID" from "YubiKey #2".
//
//   webauthn_challenges       — short-lived (5 min) challenges minted
//                               by the registration / sign endpoints.
//                               One row per server-issued challenge so
//                               the verify step can prove the assertion
//                               was actually elicited by us (replay
//                               protection independent of session
//                               cookies).
//
//   tenant_step_up_policy     — single-row-per-tenant policy that lets
//                               a tenant administrator (a) raise the
//                               minimum step-up factor for high-stakes
//                               PTW actions above the platform default
//                               and (b) forbid the WebAuthn / password
//                               fallback when WorkOS step-up is
//                               temporarily unavailable. This matches
//                               the regulator-mandated assurance floor
//                               for HSE-regulated tenants.
//
// All three tables degrade gracefully when absent: the resolver in
// `server/services/step-up-policy.ts` returns the platform default
// when the row is missing, and the WebAuthn endpoints surface a
// friendly 503 if no credentials have been enrolled for the actor
// (the engine then falls through to the next assurance tier the
// step config permits).

/**
 * Allowed minimum step-up factors. The order is the canonical
 * hierarchy used by `meetsStepUpFloor` in
 * `server/services/step-up-policy.ts`:
 *
 *   password ≤ password+totp ≤ webauthn ≤ webauthn+presence ≤ workos_step_up
 *
 * `password` matches the legacy CAP-5 re-auth tier; `password+totp`
 * additionally requires the actor's MFA to be enabled and recently
 * verified; `webauthn` accepts a passkey assertion; `webauthn+presence`
 * requires the assertion to be flagged userVerified=true; and
 * `workos_step_up` is the IdP-driven step-up that returns a
 * fresh-authentication factor stamp from WorkOS.
 */
export const STEP_UP_FACTORS = [
  "password",
  "password+totp",
  "webauthn",
  "webauthn+presence",
  "workos_step_up",
] as const;
export type StepUpFactor = (typeof STEP_UP_FACTORS)[number];

export const webauthnCredentials = pgTable("webauthn_credentials", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  /**
   * Tenant scope at registration time (forensic only — credentials are
   * always usable by the owning user across tenant switches).
   */
  tenantId: text("tenant_id"),
  /**
   * `credential_id` is the base64url-encoded credential id minted by the
   * authenticator. Unique globally — two users cannot share the same
   * credential id (the WebAuthn spec guarantees uniqueness because the
   * id is a hash of the authenticator + relying-party).
   */
  credentialId: text("credential_id").notNull().unique(),
  /** Public key in base64url-encoded COSE format. */
  publicKey: text("public_key").notNull(),
  /** Cloned-authenticator detection — compared against the assertion. */
  signCounter: integer("sign_counter").notNull().default(0),
  /** "platform" (Touch ID / Windows Hello) | "cross-platform" (USB key). */
  deviceType: text("device_type").notNull().default("unknown"),
  /** Comma-separated transports list ("usb,nfc,ble,internal"). */
  transports: text("transports"),
  /** Attestation format reported by the authenticator. */
  attestationFormat: text("attestation_format").notNull().default("none"),
  /** Whether the credential is backed by a backup-eligible key store. */
  backupEligible: boolean("backup_eligible").notNull().default(false),
  /** Whether the credential has actually been backed up (synced). */
  backupState: boolean("backup_state").notNull().default(false),
  /** Whether the assertion required user verification (PIN / biometric). */
  userVerified: boolean("user_verified").notNull().default(false),
  /** Friendly device label set by the user at enrolment time. */
  friendlyName: text("friendly_name"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  lastUsedAt: timestamp("last_used_at", { withTimezone: true }),
  revokedAt: timestamp("revoked_at", { withTimezone: true }),
}, (t) => ({
  idxWebauthnCredentialsUser: index("idx_webauthn_credentials_user").on(t.userId),
  idxWebauthnCredentialsActive: index("idx_webauthn_credentials_active").on(t.userId, t.revokedAt),
}));
export const insertWebauthnCredentialSchema = createInsertSchema(webauthnCredentials).omit({
  id: true,
  createdAt: true,
  lastUsedAt: true,
  revokedAt: true,
});
export type InsertWebauthnCredential = z.infer<typeof insertWebauthnCredentialSchema>;
export type WebauthnCredential = typeof webauthnCredentials.$inferSelect;

export const webauthnChallenges = pgTable("webauthn_challenges", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  /** "registration" | "assertion" — discriminator for the verify step. */
  purpose: text("purpose").notNull(),
  /** base64url-encoded challenge bytes the authenticator signs over. */
  challenge: text("challenge").notNull(),
  /** Optional permit binding when challenge gates a PTW action. */
  permitId: text("permit_id"),
  permitVersion: integer("permit_version"),
  stepInstanceId: text("step_instance_id"),
  ipAddress: text("ip_address"),
  deviceFingerprint: text("device_fingerprint"),
  userAgent: text("user_agent"),
  issuedAt: timestamp("issued_at", { withTimezone: true }).notNull().defaultNow(),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  consumedAt: timestamp("consumed_at", { withTimezone: true }),
}, (t) => ({
  idxWebauthnChallengesUser: index("idx_webauthn_challenges_user").on(t.userId, t.issuedAt),
  idxWebauthnChallengesExpires: index("idx_webauthn_challenges_expires").on(t.expiresAt),
}));
export const insertWebauthnChallengeSchema = createInsertSchema(webauthnChallenges).omit({
  id: true,
  issuedAt: true,
  consumedAt: true,
});
export type InsertWebauthnChallenge = z.infer<typeof insertWebauthnChallengeSchema>;
export type WebauthnChallenge = typeof webauthnChallenges.$inferSelect;

export const tenantStepUpPolicy = pgTable("tenant_step_up_policy", {
  tenantId: text("tenant_id")
    .primaryKey()
    .references(() => tenants.id, { onDelete: "cascade" }),
  /**
   * Tenant-wide minimum step-up factor for PTW high-stakes actions
   * (issue / override / close / second-actor). Per-step overrides in
   * `ptw_workflow_steps.config.minStepUpFactor` may raise this floor
   * but cannot lower it.
   */
  defaultMinFactor: text("default_min_factor").notNull().default("password"),
  /**
   * When true, the WebAuthn / password fallback is REFUSED when the
   * step config or tenant default requires `workos_step_up`. When
   * false (default), the engine accepts the next-strongest available
   * assurance tier and emits `auth.stepup_fallback_used`.
   *
   * Tenants in HSE-regulated jurisdictions typically enable this to
   * guarantee that "the WorkOS step-up was actually performed" rather
   * than admitting a webauthn assertion as a substitute.
   */
  forbidFallback: boolean("forbid_fallback").notNull().default(false),
  /** Whether webauthn enrolment is allowed for users in this tenant. */
  webauthnEnabled: boolean("webauthn_enabled").notNull().default(true),
  /**
   * Optional override of the default 5-minute challenge TTL (ms).
   * Bounded server-side to [60_000, 1_800_000] (1 min .. 30 min).
   */
  challengeTtlMs: integer("challenge_ttl_ms"),
  /** Free-form metadata (admin notes, audit trail crumbs). */
  metadata: jsonb("metadata").notNull().default(sql`'{}'::jsonb`),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  updatedByUserId: text("updated_by_user_id").references(() => users.id, { onDelete: "set null" }),
}, (t) => ({
  idxTenantStepUpPolicyDefault: uniqueIndex("uq_tenant_step_up_policy_tenant").on(t.tenantId),
}));
export const insertTenantStepUpPolicySchema = createInsertSchema(tenantStepUpPolicy).omit({
  updatedAt: true,
});
export type InsertTenantStepUpPolicy = z.infer<typeof insertTenantStepUpPolicySchema>;
export type TenantStepUpPolicy = typeof tenantStepUpPolicy.$inferSelect;
